import React, { useEffect, useState } from 'react';
import { 
    Box, 
    Card, 
    CardBody, 
    CardHeader, 
    Heading, 
    Spinner, 
    Center, 
    Text, 
    VStack 
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { HelperForm } from '@/components/forms/HelperForm';
import { apiClient } from '@/lib/api/client';
import { useToastNotification } from '@/lib/utils/useToastNotification';

const EditHelperPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { showError } = useToastNotification();
    
    const [helperData, setHelperData] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);

    const handleBack = () => navigate('/fleet-management/helpers');

    useEffect(() => {
        const fetchHelper = async () => {
            try {
                setIsLoading(true);
                const response = await apiClient.get(`/helpers/${id}`);
                setHelperData(response.data.data || response.data);
            } catch (error: any) {
                showError(error);
                handleBack(); 
            } finally {
                setIsLoading(false);
            }
        };

        if (id) fetchHelper();
    }, [id, showError]);

    return (
        <AdminLayout>
            {/* 1. Container: Full viewport height, hidden overflow, flex layout */}
            <Box p={4} h="full" overflow="hidden" display="flex" flexDirection="column">
                
                {/* 2. Card: Fills container and splits into Header/Body */}
                <Card h="full" display="flex" flexDirection="column" variant="outline" boxShadow="sm">
                    
                    {/* 3. Header: Fixed at the top, stays visible on shrink */}
                    <CardHeader py={4} borderBottom="1px" borderColor="gray.100" flexShrink={0}>
                        <Heading size="md" mb={1}>
                            Edit Helper: {helperData ? `${helperData.first_name} ${helperData.last_name}` : '...'}
                        </Heading>
                        <Text fontSize="sm" color="gray.500">
                            Update the helper's profile and contact information.
                        </Text>
                    </CardHeader>
                    
                    {/* 4. Body: Scrollable area for the form */}
                    <CardBody py={6} overflowY="auto" flex="1" minH={0}>
                        {isLoading ? (
                            <Center h="full">
                                <VStack>
                                    <Spinner color="blue.500" size="xl" thickness="4px" />
                                    <Text mt={4} color="gray.500">Fetching helper details...</Text>
                                </VStack>
                            </Center>
                        ) : (
                            <HelperForm 
                                key={helperData?.id} // Forces re-mount when data is ready
                                helperId={Number(id)} 
                                initialData={helperData} 
                                onSuccess={handleBack} 
                                onCancel={handleBack} 
                            />
                        )}
                    </CardBody>
                </Card>
            </Box>
        </AdminLayout>
    );
};

export default EditHelperPage;
